import type { CommonRequestFn } from "@marketplace/http";
import type { BaseResponse } from "@marketplace/shared/types";

import { ENDPOINTS } from "@/constants/endpoints";
import { httpClient } from "@/http";

import type { CreateAutomationRequestDto } from "../dtos/create-automation-request-dto";
import type { GetAutomationDetailRequestDto } from "../dtos/get-automation-detail-request-dto";
import type { GetAutomationDetailResponseDto } from "../dtos/get-automation-detail-response-dto";
import type { GetAutomationListRequestDto } from "../dtos/get-automation-list-request-dto";
import type { GetAutomationListResponseDto } from "../dtos/get-automation-list-response-dto";
import type { GetAutomationSummaryResponseDto } from "../dtos/get-automation-summary-response-dto";
import type { SetupAutomationNotifyRequestDto } from "../dtos/setup-automation-notify-request-dto";

type AutomationServiceContract = {
  getAutomationList: CommonRequestFn<
    GetAutomationListRequestDto,
    GetAutomationListResponseDto
  >;
  getAutomationDetail: CommonRequestFn<
    GetAutomationDetailRequestDto,
    GetAutomationDetailResponseDto
  >;
  getAutomationSummary: CommonRequestFn<
    GetAutomationDetailRequestDto,
    GetAutomationSummaryResponseDto
  >;
  createAutomation: (args: {
    payload: CreateAutomationRequestDto;
  }) => Promise<BaseResponse<undefined>>;
  updateAutomation: (args: {
    id: string;
    payload: CreateAutomationRequestDto;
  }) => Promise<BaseResponse<undefined>>;
  deleteAutomation: (args: {
    payload: { id: string };
  }) => Promise<BaseResponse<undefined>>;
  setupAutomationNotify: (args: {
    payload: SetupAutomationNotifyRequestDto;
  }) => Promise<BaseResponse<undefined>>;
  getAutomationNotifySetup: CommonRequestFn<
    { id: string },
    BaseResponse<SetupAutomationNotifyRequestDto>
  >;
  updateAutomationNotify: (args: {
    payload: SetupAutomationNotifyRequestDto;
  }) => Promise<BaseResponse<undefined>>;
};

export const AutomationService: AutomationServiceContract = {
  getAutomationList: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.AUTOMATION.LIST, {
        ...options,
        searchParams: {
          ...payload,
          sort: payload.sort ?? "newest"
        }
      })
      .json<GetAutomationListResponseDto>();
  },

  getAutomationDetail: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.AUTOMATION.DETAIL(payload.id), options)
      .json<GetAutomationDetailResponseDto>();
  },

  getAutomationSummary: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.AUTOMATION.SUMMARY(payload.id), options)
      .json<GetAutomationSummaryResponseDto>();
  },

  createAutomation: async ({ payload }) =>
    httpClient
      .post(ENDPOINTS.AUTOMATION.LIST, {
        json: payload
      })
      .json<BaseResponse<undefined>>(),

  updateAutomation: async ({ id, payload }) =>
    httpClient
      .put(ENDPOINTS.AUTOMATION.UPDATE(id), {
        json: payload
      })
      .json<BaseResponse<undefined>>(),

  deleteAutomation: async ({ payload }) =>
    httpClient
      .delete(ENDPOINTS.AUTOMATION.DELETE(payload.id))
      .json<BaseResponse<undefined>>(),

  setupAutomationNotify: async ({ payload }) =>
    httpClient
      .post(ENDPOINTS.AUTOMATION.NOTIFY_SETUP, {
        json: payload
      })
      .json<BaseResponse<undefined>>(),

  getAutomationNotifySetup: ({ payload, options }) => {
    return httpClient
      .get(ENDPOINTS.AUTOMATION.NOTIFY_SETUP_DETAIL(payload.id), options)
      .json<BaseResponse<SetupAutomationNotifyRequestDto>>();
  },

  updateAutomationNotify: async ({ payload }) =>
    httpClient
      .put(ENDPOINTS.AUTOMATION.NOTIFY_SETUP_DETAIL(payload.product_id), {
        json: payload
      })
      .json<BaseResponse<undefined>>()
};
